/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	ChangeDetectorRef,
	Component,
	HostListener,
	OnInit
} from '@angular/core';
import {
	ExtendedCustomControlDirective
} from '@entity/directives/extended-custom-control.directive';
import {
	AppEventParameterConstants
} from '@shared/constants/app-event-parameter.constants';
import {
	AppEventConstants
} from '@shared/constants/app-event.constants';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	DateHelper
} from '@shared/helpers/date.helper';
import {
	FormlyHelper
} from '@shared/helpers/formly.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	DateTime
} from 'luxon';

@Component({
	selector: 'custom-calendar',
	templateUrl: './custom-calendar.component.html',
	styleUrls: [
		'./custom-calendar.component.scss'
	]
})

/**
 * A component representing an instance of a Custom Calendar.
 * https://ngx-formly.github.io/ngx-formly/guide
 *
 * @export
 * @class CustomCalendarComponent
 * @extends {ExtendedCustomControlDirective}
 * @implements {OnInit}
 */
export class CustomCalendarComponent
	extends ExtendedCustomControlDirective
	implements OnInit
{
	/** Initializes a new instance of the CustomCalendarComponent.
	 *
	 * @param {ChangeDetectorRef} changeDetector
	 * The change detector reference for this component.
	 * @memberof CustomCalendarComponent
	 */
	public constructor(
		public changeDetector: ChangeDetectorRef)
	{
		super(changeDetector);
	}

	/**
	 * Gets or sets the display date.
	 *
	 * @type {Date}
	 * @memberof CustomCalendarComponent
	 */
	public displayDate: Date;

	/**
	 * Cached format metadata computed once during initialization.
	 * Since dateFormat never changes at runtime, this is computed once and reused.
	 *
	 * @private
	 * @memberof CustomCalendarComponent
	 */
	private formatMetadata: {
		hasMonth: boolean;
		hasDay: boolean;
		hasYear: boolean;
		yearDigits: number;
		monthDigits: number;
		dayDigits: number;
		dateDigits: number;
		maxDigitsWithTime: number;
		maxDigitsWithoutTime: number;
		validationPatternWithTime: RegExp;
		validationPatternWithoutTime: RegExp;
	};

	/**
	 * Gets or sets the refesh calendar value. This is used to ensure a bug in
	 * primeNg layout change closed calendars no longer reopen on next click.
	 *
	 * @type {boolean}
	 * @memberof CustomCalendarComponent
	 */
	public refreshCalendar: boolean = false;

	/**
	 * Handles the site layout change event which is called
	 * when the site layout service has altered it's variables.
	 *
	 * @memberof CustomCalendarComponent
	 */
	@HostListener(
		AppEventConstants.siteLayoutChangedEvent)
	public siteLayoutChanged(): void
	{
		// This timeout chain is used to redraw the calendar without flashing.
		// This method can be removed when PrimeNG corrects their calendar.
		setTimeout(() => {
			this.refreshCalendar = true;
		});
		setTimeout(() => {
			this.refreshCalendar = false;
		});
	}

	/**
	 * Handles the hide associated menus event.
	 * This is used to close this calendar when an associated menu is
	 * opened via a non bubbled event, such as a sibling navigation menu.
	 *
	 * @memberof AppProfileComponent
	 */
	@HostListener(
		AppEventConstants.hideAssociatedMenusEvent,
		[AppEventParameterConstants.id])
	public hideAssociatedMenus(
		_controlIdentifer: string): void
	{
		this.siteLayoutChanged();
	}

	/**
	 * Handles the on initialization interface.
	 * This will capture the saved date and time, and translate
	 * this into a date and locale time for use in this component.
	 *
	 * @memberof CustomCalendarComponent
	 */
	public ngOnInit(): void
	{
		this.field.props.setDisplayDate =
			this.setDisplayDate.bind(this);

		this.computeFormatMetadata();

		const shouldUseDefaultFunction: boolean =
			this.field.props.useDefaultFunction === true
			&& AnyHelper.isNullOrWhitespace(
				this.field.formControl.value)
			&& !AnyHelper.isNullOrWhitespace(
				this.field.props.default);

		if (shouldUseDefaultFunction)
		{
			const evaluatedDefault: string =
				this.resolveDefaultValue();
			this.initializeWithEvaluatedDefault(evaluatedDefault);
			return;
		}

		this.setDisplayDate(
			AnyHelper.isNullOrWhitespace(this.field.formControl.value)
				&& !AnyHelper.isNullOrWhitespace(
					this.field.props.default)
				? this.field.props.default
				: this.field.formControl.value);

		if (AnyHelper.isNullOrWhitespace(this.displayDate)
			&& this.field.props.required === true)
		{
			this.field.props.disabled = false;
		}

		super.ngOnInit();
	}

	/**
	 * Sets a new calendar display date.
	 *
	 * @param {string} displayDate
	 * The date string on UTC ISO.
	 * @memberof CustomCalendarComponent
	 */
	public setDisplayDate(displayDate: string = null): void
	{
		if (DateHelper.fromSimulatedLocalDateToUtcSystemIso(
			DateTime.fromJSDate(this.displayDate)
				.toISO()) === displayDate)
		{
			return;
		}

		this.displayDate =
			!AnyHelper.isNullOrWhitespace(displayDate)
				? DateHelper.fromUtcSystemIsoToSimulatedLocalDate(
					displayDate)
				: null;

		if (this.formatMetadata)
		{
			this.dateChange(null);
		}
	}

	/**
	 * Handles the calendar input event with masking logic.
	 *
	 * @param {Event} event
	 * The event that sent this action.
	 * @memberof CustomCalendarComponent
	 */
	public handleInput(
		event: Event): void
	{
		if (AnyHelper.isNull(this.formatMetadata))
		{
			return;
		}
		
		const inputElement: HTMLInputElement = event.target as HTMLInputElement;
		if (AnyHelper.isNull(inputElement))
		{
			this.dateChange(event);
			return;
		}

		const value: string = inputElement.value;
		
		if (this.isValidFormat(value, this.field.props.showTime))
		{
			this.dateChange(event);
			return;
		}
		
		const digitsOnly: string = value.replaceAll(/\D/g, '');
		const digitsBefore: number = this.countDigitsBeforeCursor(value, inputElement.selectionStart || 0);
		const maxDigits: number = this.getMaxDigits();
		const masked: string = this.buildMaskedString(digitsOnly, maxDigits);
		
		if (value !== masked)
		{
			inputElement.value = masked;
			const newCursorPos: number = this.calculateCursorPosition(masked, digitsBefore);
			inputElement.setSelectionRange(newCursorPos, newCursorPos);
		}
		
		if (digitsOnly.length >= maxDigits)
		{
			this.updateDisplayDateFromMasked(masked);
			this.dateChange(event);
		}
	}

	/**
	 * Computes and caches format metadata during initialization.
	 * This eliminates repeated regex operations during user input.
	 *
	 * @private
	 * @memberof CustomCalendarComponent
	 */
	private computeFormatMetadata(): void
	{
		const dateFormat: string = this.field.props.dateFormat || 'mm/dd/yy';
		
		const hasMonth: boolean = /m/i.test(dateFormat);
		const hasDay: boolean = /d/i.test(dateFormat);
		const hasYear: boolean = /y/i.test(dateFormat);
		
		const yearDigits: number = this.extractYearDigits(dateFormat);
		const { monthDigits, dayDigits, dateDigits } = this.calculateDigitCounts(
			hasMonth,
			hasDay,
			hasYear,
			yearDigits);
		
		const datePatternStr: string = this.buildDatePatternString(
			hasMonth,
			hasDay,
			hasYear,
			yearDigits);
		
		const { validationPatternWithTime, validationPatternWithoutTime } =
			this.compileValidationPatterns(datePatternStr);
		
		this.formatMetadata = {
			hasMonth,
			hasDay,
			hasYear,
			yearDigits,
			monthDigits,
			dayDigits,
			dateDigits,
			maxDigitsWithTime: dateDigits + 6,
			maxDigitsWithoutTime: dateDigits,
			validationPatternWithTime,
			validationPatternWithoutTime
		};
	}

	/**
	 * Extracts the number of year digits from the date format string.
	 *
	 * @private
	 * @param {string} dateFormat
	 * The date format string.
	 * @returns {number}
	 * The number of year digits (2 or 4) or 0 if no year is expected.
	 * @memberof CustomCalendarComponent
	 */
	private extractYearDigits(dateFormat: string): number
	{
		const yearPattern: RegExp = /y+/i;
		const yearMatch: RegExpExecArray = yearPattern.exec(dateFormat);
		
		if (!yearMatch)
		{
			return 0;
		}
		
		const matchLength: number = yearMatch[0].length;
		return matchLength === 1 ? 2 : 4;
	}

	/**
	 * Calculates digit counts based on which date components are present.
	 *
	 * @private
	 * @param {boolean} hasMonth
	 * Whether the format includes month.
	 * @param {boolean} hasDay
	 * Whether the format includes day.
	 * @param {boolean} hasYear
	 * Whether the format includes year.
	 * @param {number} yearDigits
	 * The number of year digits.
	 * @returns {{ monthDigits: number; dayDigits: number; dateDigits: number }}
	 * The calculated digit counts.
	 * @memberof CustomCalendarComponent
	 */
	private calculateDigitCounts(
		hasMonth: boolean,
		hasDay: boolean,
		hasYear: boolean,
		yearDigits: number): { monthDigits: number; dayDigits: number; dateDigits: number }
	{
		let dateDigits: number = 0;
		let monthDigits: number = 0;
		let dayDigits: number = 0;
		
		if (hasMonth && hasDay && hasYear)
		{
			monthDigits = 2;
			dayDigits = 2;
			dateDigits = 4 + yearDigits;
		}
		else if (hasMonth && hasDay)
		{
			monthDigits = 2;
			dayDigits = 2;
			dateDigits = 4;
		}
		else if (hasMonth && hasYear)
		{
			monthDigits = 2;
			dateDigits = 2 + yearDigits;
		}
		else if (hasYear)
		{
			dateDigits = yearDigits;
		}
		
		return { monthDigits, dayDigits, dateDigits };
	}

	/**
	 * Builds the date pattern string for validation.
	 *
	 * @private
	 * @param {boolean} hasMonth
	 * Whether the format includes month.
	 * @param {boolean} hasDay
	 * Whether the format includes day.
	 * @param {boolean} hasYear
	 * Whether the format includes year.
	 * @param {number} yearDigits
	 * The number of year digits.
	 * @returns {string}
	 * The date pattern string.
	 * @memberof CustomCalendarComponent
	 */
	private buildDatePatternString(
		hasMonth: boolean,
		hasDay: boolean,
		hasYear: boolean,
		yearDigits: number): string
	{
		const yearPattern: string = yearDigits === 4 ? String.raw`\d{4}` : String.raw`\d{2}`;
		
		if (hasMonth && hasDay && hasYear)
		{
			return String.raw`^\d{2}\/\d{2}\/${yearPattern}`;
		}
		if (hasMonth && hasDay)
		{
			return String.raw`^\d{2}\/\d{2}`;
		}
		if (hasMonth && hasYear)
		{
			return String.raw`^\d{2}\/${yearPattern}`;
		}
		if (hasYear)
		{
			return `^${yearPattern}`;
		}
		
		return '';
	}

	/**
	 * Compiles validation patterns for date formats with and without time.
	 *
	 * @private
	 * @param {string} datePatternStr
	 * The date pattern string.
	 * @returns {{ validationPatternWithTime: RegExp; validationPatternWithoutTime: RegExp }}
	 * The compiled validation patterns.
	 * @memberof CustomCalendarComponent
	 */
	private compileValidationPatterns(
		datePatternStr: string): { validationPatternWithTime: RegExp; validationPatternWithoutTime: RegExp }
	{
		const validationPatternWithoutTime: RegExp = datePatternStr
			? new RegExp(`${datePatternStr}$`)
			: null;
			
		const validationPatternWithTime: RegExp = datePatternStr
			? new RegExp(String.raw`${datePatternStr} \d{2}:\d{2}:\d{2}$`)
			: null;
		
		return { validationPatternWithTime, validationPatternWithoutTime };
	}

	/**
	 * Counts the number of digits before the cursor position.
	 *
	 * @private
	 * @param {string} value
	 * The input value.
	 * @param {number} cursorPos
	 * The cursor position.
	 * @returns {number}
	 * The count of digits before the cursor.
	 * @memberof CustomCalendarComponent
	 */
	private countDigitsBeforeCursor(
		value: string,
		cursorPos: number): number
	{
		const beforeCursor: string = value.substring(0, cursorPos);
		return (beforeCursor.match(/\d/g) || []).length;
	}

	/**
	 * Gets the maximum number of digits allowed based on the current format.
	 *
	 * @private
	 * @returns {number}
	 * The maximum number of digits.
	 * @memberof CustomCalendarComponent
	 */
	private getMaxDigits(): number
	{
		return this.field.props.showTime 
			? this.formatMetadata.maxDigitsWithTime 
			: this.formatMetadata.maxDigitsWithoutTime;
	}

	/**
	 * Builds a masked string with appropriate separators.
	 *
	 * @private
	 * @param {string} digitsOnly
	 * The digits to mask.
	 * @param {number} maxDigits
	 * The maximum number of digits allowed.
	 * @returns {string}
	 * The masked string.
	 * @memberof CustomCalendarComponent
	 */
	private buildMaskedString(
		digitsOnly: string,
		maxDigits: number): string
	{
		let masked: string = AppConstants.empty;
		
		for (let index = 0; index < digitsOnly.length && index < maxDigits; index++)
		{
			const separator: string = this.getSeparatorAt(index);
			if (separator)
			{
				masked += separator;
			}
			masked += digitsOnly[index];
		}
		
		return masked;
	}

	/**
	 * Gets the separator character at the specified position, if any.
	 *
	 * @private
	 * @param {number} position
	 * The current position in the digit sequence.
	 * @returns {string}
	 * The separator character or empty string.
	 * @memberof CustomCalendarComponent
	 */
	private getSeparatorAt(position: number): string
	{
		if (this.formatMetadata.hasMonth && this.formatMetadata.hasDay 
			&& position === this.formatMetadata.monthDigits)
		{
			return '/';
		}
		
		if (this.formatMetadata.hasMonth && this.formatMetadata.hasDay 
			&& this.formatMetadata.hasYear 
			&& position === this.formatMetadata.monthDigits + this.formatMetadata.dayDigits)
		{
			return '/';
		}
		
		if (this.formatMetadata.hasMonth && !this.formatMetadata.hasDay 
			&& this.formatMetadata.hasYear 
			&& position === this.formatMetadata.monthDigits)
		{
			return '/';
		}
		
		if (position === this.formatMetadata.dateDigits && this.field.props.showTime)
		{
			return ' ';
		}
		
		if (this.field.props.showTime 
			&& (position === this.formatMetadata.dateDigits + 2 
			|| position === this.formatMetadata.dateDigits + 4))
		{
			return ':';
		}
		
		return '';
	}

	/**
	 * Calculates the new cursor position after masking.
	 *
	 * @private
	 * @param {string} masked
	 * The masked string.
	 * @param {number} digitsBefore
	 * The number of digits before the original cursor position.
	 * @returns {number}
	 * The new cursor position.
	 * @memberof CustomCalendarComponent
	 */
	private calculateCursorPosition(
		masked: string,
		digitsBefore: number): number
	{
		let newCursorPos: number = 0;
		let digitsEncountered: number = 0;
		
		for (let index = 0; index < masked.length && digitsEncountered < digitsBefore; index++)
		{
			if (/\d/.test(masked[index]))
			{
				digitsEncountered++;
			}
			newCursorPos = index + 1;
		}
		
		while (newCursorPos < masked.length && /[/: ]/.test(masked[newCursorPos]))
		{
			newCursorPos++;
		}
		
		return Math.min(newCursorPos, masked.length);
	}

	/**
	 * Updates the display date from the masked input value.
	 *
	 * @private
	 * @param {string} masked
	 * The masked string to parse.
	 * @memberof CustomCalendarComponent
	 */
	private updateDisplayDateFromMasked(masked: string): void
	{
		// Parse the masked string directly using Luxon's fromFormat
		// PrimeNG format quirks: 'mm' = month, 'yy' = 4-digit year, 'y' = 2-digit year
		// Luxon format: 'MM' = month, 'yyyy' = 4-digit year, 'yy' = 2-digit year
		const primeNgFormat: string = this.field.props.dateFormat || 'mm/dd/yy';
		const luxonFormat: string = primeNgFormat
			.replaceAll(/mm/gi, 'MM')
			.replaceAll('yy', 'yyyy')
			.replaceAll(/\by\b/g, 'yy');
		const fullFormat: string = this.field.props.showTime
			? `${luxonFormat} hh:mm:ss`
			: luxonFormat;
		
		const parsedDateTime: DateTime = DateTime.fromFormat(
			masked,
			fullFormat,
			{ zone: DateHelper.getLocalTimeZone() });
		
		if (parsedDateTime.isValid)
		{
			// Convert to simulated local date for PrimeNG calendar
			this.displayDate = DateHelper.fromUtcSystemIsoToSimulatedLocalDate(
				parsedDateTime.toISO());
		}
	}

	/**
	 * Checks if a value is already in a valid format.
	 * This prevents re-processing values that PrimeNG has already formatted.
	 *
	 * @param {string} value
	 * The value to check.
	 * @param {boolean} showTime
	 * Whether time is included.
	 * @returns {boolean}
	 * True if the value matches the expected format.
	 * @memberof CustomCalendarComponent
	 */
	private isValidFormat(
		value: string,
		showTime: boolean): boolean
	{
		if (AnyHelper.isNullOrWhitespace(value) || AnyHelper.isNull(this.formatMetadata))
		{
			return false;
		}

		const pattern: RegExp = showTime
			? this.formatMetadata.validationPatternWithTime
			: this.formatMetadata.validationPatternWithoutTime;
		
		return AnyHelper.isNull(pattern) ? false : pattern.test(value);
	}

	/**
	 * Handles the calendar input or select date event.
	 *
	 * @param {Event} event
	 * The event that sent this action.
	 * @memberof CustomCalendarComponent
	 */
	public dateChange(
		event: Event): void
	{
		const inputDate: string = (<any>event?.target)?.value;

		if (this.field.props?.view === DateHelper.timeUnits.year
			&& !AnyHelper.isNullOrWhitespace(inputDate))
		{
			const inputYear: number = parseInt(
				inputDate,
				AppConstants.parseRadix);

			if (inputYear >= 1000 && inputYear <= 9999)
			{
				const yearDateTime: DateTime =
					DateHelper.startOf(
						DateHelper.fromUtcIso(
							`${inputYear}-06-01T00:00:00.000`),
						DateHelper.timeUnits.year);

				this.displayDate = DateHelper
					.fromUtcSystemIsoToSimulatedLocalDate(
						yearDateTime.toISO());
			}
		}

		const newDateIso: string =
			!AnyHelper.isNull(this.displayDate)
				? DateHelper.fromSimulatedLocalDateToUtcSystemIso(
					DateTime.fromJSDate(
						this.displayDate)
						.toISO())
				: null;

		this.field.formControl.setValue(newDateIso);
		this.validateControl();

		if (!AnyHelper.isNull(this.field.props.change))
		{
			this.field.props.change(
				this.field,
				event);
		}
	}

	/**
	 * Resolves the default value for the calendar,
	 * evaluating functions if necessary.
	 *
	 * @private
	 * @returns {string | null}
	 * The resolved default value or null if not applicable.
	 * @memberof CustomCalendarComponent
	 */
	private resolveDefaultValue(): string | null
	{
		if (this.field.props.useDefaultFunction !== true)
		{
			return null;
		}

		if (AnyHelper.isFunction(
			this.field.props.default))
		{
			return this.field.props.default();
		}

		if (AnyHelper.isNull(this.field.props.context?.source?.resolver))
		{
			return null;
		}

		const defaultFunction: Function =
			StringHelper.transformToFunction(
				this.field.props.default,
				this.field.props.context);

		return defaultFunction();
	}

	/**
	 * Initializes the component when useDefaultFunction is enabled.
	 * Manually performs parent initialization without default value
	 * assignment to avoid processing the raw function string.
	 *
	 * @private
	 * @param {string} evaluatedDefault
	 * The evaluated default value from the function.
	 * @memberof CustomCalendarComponent
	 */
	private initializeWithEvaluatedDefault(
		evaluatedDefault: string): void
	{
		FormlyHelper.extendUpdateValueAndValidity(
			this.field.formControl,
			this.updateCustomDisplay.bind(this));

		if (!AnyHelper.isNullOrWhitespace(evaluatedDefault))
		{
			this.field.formControl.setValue(evaluatedDefault);
			this.setDisplayDate(evaluatedDefault);
		}

		this.validateControl();
	}
}